import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router";
import { downloadAdminFile } from "../../app/files";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen, pathForAdminScreen } from "../../app/routes";
import type { Command } from "../workspace/model";
import { StateNotice } from "../workspace/shared";
import { TransactionDetailView } from "./components/TransactionDetailView";
import { RecipientAccountReveal } from "./RecipientAccountReveal";
import type { TransactionDocumentPreview } from "./components/TransactionDetailView";
import {
  useClaimTransactionReview,
  useDecideTransactionReview,
  useRequestTransactionSupplement,
  useTransactionDetail,
} from "./queries";

function detailState(
  query: ReturnType<typeof useTransactionDetail>,
): "ready" | "loading" | "error" | "not-found" {
  if (query.isPending) return "loading";
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found";
  if (query.isError) return "error";
  return "ready";
}

function TransactionDetailPageForTransaction({ transactionId }: { readonly transactionId: number }) {
  const services = useAdminApplication();
  const session = useAdminSession();
  const navigation = useAdminNavigation({ contextKey: "transactions", defaultListPath: pathForAdminScreen("07"), originLabel: "거래 상세" });
  const detail = useTransactionDetail(transactionId);
  const claim = useClaimTransactionReview(transactionId);
  const decision = useDecideTransactionReview(transactionId);
  const supplement = useRequestTransactionSupplement(transactionId);
  const canReview = (session.authSession?.authorities ?? []).includes("TRANSACTION_REVIEW");
  const fileRequests = useRef(new Set<AbortController>());

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const refresh = async () => {
    const result = await detail.refetch();
    if (result.error) throw result.error;
    return result.data;
  };

  const execute = async (command: Command) => {
    switch (command.operation) {
      case "POST /api/v1/admin/transactions/{transactionId}/reviews/claim":
        return claim.mutateAsync();
      case "POST /api/v1/admin/transactions/{transactionId}/reviews/decision":
        return decision.mutateAsync(command.body);
      case "POST /api/v1/admin/transactions/{transactionId}/reviews/supplement-requests":
        return supplement.mutateAsync(command.body);
      default:
        throw new Error(`Unsupported transaction command: ${command.operation}`);
    }
  };

  const downloadDocument = async (
    document: { readonly fileAssetId: number },
  ): Promise<TransactionDocumentPreview> => {
    const controller = new AbortController();
    fileRequests.current.add(controller);
    try {
      const blob = await downloadAdminFile(services, document.fileAssetId, {
        businessRefType: "TRANSACTION",
        businessRefId: transactionId,
      }, controller.signal);
      return {
        url: services.sessionLifecycle.createPreviewUrl(blob),
        contentType: blob.type || "application/octet-stream",
      };
    } finally {
      fileRequests.current.delete(controller);
    }
  };

  const releasePreview = useCallback((url: string) => {
    services.sessionLifecycle.revokePreviewUrl(url);
  }, [services.sessionLifecycle]);

  return (
    <TransactionDetailView
      detail={detail.data}
      recipientAccountAction={detail.data ? <RecipientAccountReveal key={transactionId} transactionId={transactionId} /> : undefined}
      state={detailState(detail)}
      actorId={session.actor?.id ?? 0}
      canReview={canReview}
      onBack={navigation.backToList}
      backHref={navigation.backPath}
      backLabel={navigation.backLabel}
      detailHref={pathForAdminEntityScreen("08", transactionId)}
      onRetry={() => { void detail.refetch(); }}
      onRefresh={refresh}
      onCommand={execute}
      onDownloadDocument={downloadDocument}
      onReleasePreview={releasePreview}
      memberHref={detail.data && (session.authSession?.authorities ?? []).includes("MEMBER_READ") ? pathForAdminEntityScreen("06", detail.data.transaction.memberId) : undefined}
      onOpenMember={detail.data && (session.authSession?.authorities ?? []).includes("MEMBER_READ") ? () => navigation.openRelated(pathForAdminEntityScreen("06", detail.data!.transaction.memberId), "거래 상세") : undefined}
    />
  );
}

export function TransactionDetailPage() {
  const { transactionId: rawTransactionId } = useParams();
  const transactionId = parseAdminEntityId(rawTransactionId);
  if (transactionId === null) return <StateNotice state="not-found" />;
  return <TransactionDetailPageForTransaction transactionId={transactionId} />;
}
